/**
 * Record preview cards for the Platform Configuration app.
 *
 * A config-driven registry of small "mini record" cards (pict-section-recordset >= 1.22's
 * RecordSetCardManager). When a card is registered for an entity, every foreign-key reference to that
 * entity — the "automatic dropdown" entities resolved by RecordSet's ProcessCell handler (Customer,
 * Project, Contract, Organization, …) — renders in list and read views as a clickable trigger that
 * opens an anchored popover card instead of a plain link. The association-editor row names pick the
 * same cards up automatically.
 *
 * Card shape: { Title, Subtitle?, Fields:[{ Label, Value }], Actions:[{ Label, Icon, Route }] }.
 * Every Title/Subtitle/Field Value is a field name (resolved as {~D:Record.<name>~}) OR a literal
 * {~...~} template. Action Routes use the standard PSRS read route — the app's router transparently
 * bridges the entities that own a bespoke workspace (Project, Contract, User, ObservationManifest,
 * ArtifactType) to their richer editor, so a single uniform route works everywhere.
 */

const RECORD_CARDS =
{
	Customer:
	{
		Title: 'Name',
		Fields:
		[
			{ Label: 'Code', Value: 'Code' },
			{ Label: 'Timezone', Value: 'Timezone' },
		],
		Actions:
		[
			{ Label: 'View', Icon: 'Eye', Route: '#/PSRS/Customer/View/{~D:Record.GUIDCustomer~}' },
		],
	},
	Project:
	{
		Title: 'Name',
		Subtitle: '{~D:Record.Status~}',
		Fields:
		[
			{ Label: 'External ID', Value: 'ExternalProjectID' },
		],
		Actions:
		[
			{ Label: 'View', Icon: 'Eye', Route: '#/PSRS/Project/View/{~D:Record.GUIDProject~}' },
		],
	},
	Material:
	{
		Title: 'Name',
		Subtitle: '{~D:Record.CategoryName~}',
		Fields:
		[
			{ Label: 'Material Code', Value: 'MaterialCode' },
			{ Label: 'Catalog Code', Value: 'CatalogCode' },
			{ Label: 'Item Code', Value: 'ItemCode' },
		],
		Actions:
		[
			{ Label: 'View', Icon: 'Eye', Route: '#/PSRS/Material/View/{~D:Record.GUIDMaterial~}' },
		],
	},
	Contract:
	{
		Title: 'Name',
		Subtitle: '{~D:Record.Status~}',
		Fields:
		[
			{ Label: 'Primary Project #', Value: 'PrimaryProjectNumber' },
		],
		Actions:
		[
			{ Label: 'View', Icon: 'Eye', Route: '#/PSRS/Contract/View/{~D:Record.GUIDContract~}' },
		],
	},
	Organization:
	{
		Title: 'Name',
		Subtitle: '{~D:Record.Type~}',
		Fields:
		[
			{ Label: 'Vendor Code', Value: 'VendorCode' },
			{ Label: 'City', Value: 'City' },
			{ Label: 'State', Value: 'State' },
		],
		Actions:
		[
			{ Label: 'View', Icon: 'Eye', Route: '#/PSRS/Organization/View/{~D:Record.GUIDOrganization~}' },
		],
	},
	ObservationManifest:
	{
		Title: 'DisplayName',
		Subtitle: '{~D:Record.Category~}',
		Fields:
		[
			{ Label: 'Name', Value: 'Name' },
		],
		Actions:
		[
			{ Label: 'View', Icon: 'Eye', Route: '#/PSRS/ObservationManifest/View/{~D:Record.GUIDObservationManifest~}' },
		],
	},
	ArtifactType:
	{
		Title: 'Name',
		Actions:
		[
			{ Label: 'View', Icon: 'Eye', Route: '#/PSRS/ArtifactType/View/{~D:Record.GUIDArtifactType~}' },
		],
	},
	User:
	{
		Title: '{~D:Record.NameFirst~} {~D:Record.NameLast~}',
		Subtitle: '{~D:Record.LoginID~}',
		Fields:
		[
			{ Label: 'Email', Value: 'Email' },
			{ Label: 'Role', Value: 'Role' },
		],
		Actions:
		[
			{ Label: 'View', Icon: 'User', Route: '#/PSRS/User/View/{~D:Record.GUIDUser~}' },
		],
	},
	Role:
	{
		Title: 'Name',
		Subtitle: '{~D:Record.Type~}',
		Fields:
		[
			{ Label: 'Level', Value: 'PermissionLevel' },
			{ Label: 'Description', Value: 'Description' },
		],
		Actions:
		[
			{ Label: 'View', Icon: 'Eye', Route: '#/PSRS/Role/View/{~D:Record.GUIDRole~}' },
		],
	},
	ProjectType:
	{
		Title: 'Name',
		Fields:
		[
			{ Label: 'Description', Value: 'Description' },
		],
		Actions:
		[
			{ Label: 'View', Icon: 'Eye', Route: '#/PSRS/ProjectType/View/{~D:Record.GUIDProjectType~}' },
		],
	},
	ContractType:
	{
		Title: 'Name',
		Fields:
		[
			{ Label: 'Description', Value: 'Description' },
		],
		Actions:
		[
			{ Label: 'View', Icon: 'Eye', Route: '#/PSRS/ContractType/View/{~D:Record.GUIDContractType~}' },
		],
	},
};

module.exports = { RECORD_CARDS };
